import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "ui";

import { useLogSetProgressValue } from "src/workouts/useLogSetProgressValue";
import {
  type WorkoutItemSetProviderProps,
  useWorkoutItemSave,
  workoutItemSetContext,
} from "src/workouts/WorkoutItemSetContext.shared";
import { useWorkoutPermissions } from "src/workouts/WorkoutItemsPermission";

export const LogWorkoutItemWorkoutSetProvider = observer(
  function LogWorkoutItemWorkoutSetProvider(
    props: WorkoutItemSetProviderProps,
  ) {
    const workoutItemsPermissions = useWorkoutPermissions();
    const logSetProgressValue = useLogSetProgressValue();
    const requestSave = useWorkoutItemSave();

    const value = useMemo(() => {
      return {
        getType: () => {
          return props.attribute.exerciseAttribute.type;
        },
        updateEditableValue: (value: string) => {
          logSetProgressValue({
            workoutSetId: props.workoutSet.id,
            exerciseAttributeId: props.attribute.exerciseAttributeId,
            trackedValue: value,
          });
          requestSave();
        },
        getEditableValue: () => {
          if (!props.attributeValue) {
            return "";
          }

          return props.attributeValue.trackedValue;
        },
        getEditableInputId: () => {
          return `${props.workoutSet.id}:${props.attribute.exerciseAttributeId}:${props.attribute.exerciseAttribute.type}`;
        },
        isEditableInputDisabled: () => {
          return !workoutItemsPermissions.canLogSets;
        },
        renderAssignedValue: () => {
          if (!(props.attributeValue && props.attributeValue.assignedValue)) {
            return null;
          }

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={"text-muted-foreground"}>
                  ({props.attributeValue.assignedValue})
                </div>
              </TooltipTrigger>
              <TooltipContent>Value assigned by coach.</TooltipContent>
            </Tooltip>
          );
        },
      };
    }, [
      props.attributeValue?.assignedValue,
      props.attributeValue?.trackedValue,
      props.attribute.exerciseAttribute.type,
      props.attribute.exerciseAttributeId,
      props.workoutSet.id,
      workoutItemsPermissions.canLogSets,
      requestSave,
    ]);

    return (
      <workoutItemSetContext.Provider value={value}>
        {props.children}
      </workoutItemSetContext.Provider>
    );
  },
);
